import {
  COMPANY_GREETINGS,
  COMPANY_TUTOR_GREETING,
  getItem,
  TUTOR_MOB_ID,
  TUTORIAL_ZONE_ID,
  QUEST_GREETINGS,
  QUEST_GREETINGS_BEFORE,
  MOBS,
  MobIdSchema,
  type ObjectiveProgress,
  type Quest,
  type QuestRejected,
  spellTaughtBy,
} from '@shefira/shared';
import { hasBurnt } from '../game/before-the-fire.js';
import type { QuestLog } from './quest-log.js';

import { uiFormat } from './text.js';

/**
 * The words of the quests' interface (ADR 0047): what a refusal says, how
 * an objective and a reward read, and what a townsperson says when they
 * have nothing for the player. The quests' own lines are in the data.
 */

export const QUEST_REFUSALS: Record<QuestRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  unknown: 'Cette quête n’existe pas.',
  'too-far': 'Approche-toi pour lui parler.',
  'wrong-npc': 'Ce n’est pas à cette personne qu’il faut s’adresser.',
  done: 'Tu as déjà accompli cette quête.',
  active: 'Cette quête est déjà dans ton journal.',
  locked: 'Il faut d’abord accomplir la quête précédente.',
  level: 'Ton niveau est trop bas pour cette quête.',
  full: 'Ton journal est plein : abandonne une quête d’abord.',
  'not-active': 'Cette quête n’est pas dans ton journal.',
  'not-done': 'Tu n’as pas encore tout fait.',
  'bag-full': 'Ton sac est plein : fais-y de la place pour la récompense.',
  busy: 'Un instant…',
  prologue: 'Les leçons du camp ne s’abandonnent pas.',
};

/** What a townsperson of the quests says with nothing to give nor take. */
/** The tutor and anyone the regions' files leave out (ADR 0047: `greetings` of `quests/<biome>.ts`). */
const GREETINGS: Record<string, string> = {
  ranger:
    'La forêt murmure, et je l’écoute. Reviens me voir bientôt : j’aurai du travail pour toi.',
};

/**
 * The Company's recruits have their word at the quarter (ADR 0067), and Ser
 * Givrecoeur his own there: the camp's greeting is for the camp.
 */
export function greetingOf(mobId: string, zone?: string, log?: QuestLog): string {
  if (Object.hasOwn(COMPANY_GREETINGS, mobId)) {
    return COMPANY_GREETINGS[mobId as keyof typeof COMPANY_GREETINGS];
  }
  if (mobId === TUTOR_MOB_ID && zone !== undefined && zone !== TUTORIAL_ZONE_ID) {
    return COMPANY_TUTOR_GREETING;
  }
  const before = QUEST_GREETINGS_BEFORE[mobId];
  if (before !== undefined && log?.known === true && !hasBurnt(log, before.quest)) {
    return before.line;
  }
  return QUEST_GREETINGS[mobId] ?? GREETINGS[mobId] ?? 'Bonne route, voyageur.';
}

/** A townsperson's name, from the bestiary. */
export function npcName(mobId: string): string {
  const parsed = MobIdSchema.safeParse(mobId);
  return parsed.success ? MOBS[parsed.data].name : mobId;
}

/** « Rats chassés 3/10 », « Saluer le forgeron » once done or not, as a line of the journal. */
export function objectiveLine(
  quest: Quest,
  index: number,
  progress: ObjectiveProgress | undefined,
): string {
  const objective = quest.objectives[index];
  if (objective === undefined) return '';
  const counted =
    objective.kind === 'kill' || objective.kind === 'collect' || objective.kind === 'camp';
  if (!counted || progress === undefined) return objective.label;
  const target = progress.target;
  return uiFormat('{objective} {value}/{target}', {
    objective: objective.label,
    value: Math.min(progress.value, target),
    target,
  });
}

/** The reward in one line: « 40 XP · 3 or · Potion de vie ×3 ». */
export function rewardLine(quest: Quest, xp = quest.reward.xp): string {
  const parts: string[] = [];
  if (xp > 0) parts.push(uiFormat('{xp} XP', { xp }));
  if (quest.reward.gold > 0) parts.push(uiFormat('{gold} or', { gold: quest.reward.gold }));
  for (const item of quest.reward.items) {
    const name = getItem(item.templateId)?.name ?? item.templateId;
    parts.push(item.quantity > 1 ? `${name} ×${String(item.quantity)}` : name);
  }
  const spell = spellTaughtBy(quest.id);
  if (spell !== undefined)
    parts.push(uiFormat('Enseigne le sort : {spell}', { spell: spell.name }));
  return parts.join(' · ');
}
